import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useI18n } from "../i18n/LanguageProvider";
import NewsletterForm from "../components/NewsletterForm";

const SECTOR_IMAGES = [
  "/images/lithium-jujuy.jpg",
  "/images/vaca-muerta.jpg",
  "/images/industrial.jpg",
];

export default function Home() {
  const { t } = useI18n();
  const location = useLocation();
  const whyItems = t("why.items");
  const howSteps = t("how.steps");
  const sectors = t("sectors.items");
  const transparencyPoints = t("transparency.points");

  useEffect(() => {
    const hash = location.state && location.state.scrollToHash;
    if (hash) {
      // Wait one frame so the section elements are in the DOM
      requestAnimationFrame(() => {
        const el = document.getElementById(hash);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  }, [location.state]);

  return (
    <main data-testid="home-page">
      {/* Hero */}
      <section className="relative bg-white border-b border-[#E2E8F0]">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-14 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div>
              <p className="text-xs font-bold tracking-widest uppercase text-[#C85A32] mb-5">
                {t("hero.eyebrow")}
              </p>
              <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 leading-tight">
                {t("hero.title")}
              </h1>
              <p className="mt-6 text-base text-slate-600 leading-relaxed">
                {t("hero.lead")}
              </p>
              <p className="mt-4 text-base text-slate-600 leading-relaxed">
                {t("hero.lead_2")}
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <a
                  href="#newsletter"
                  className="inline-flex items-center justify-center h-12 px-6 bg-[#0F2537] hover:bg-[#0a1c2b] text-white font-semibold text-sm tracking-wide rounded-sm"
                  data-testid="hero-cta-primary"
                >
                  {t("hero.cta_primary")}
                </a>
                <a
                  href="#process"
                  className="inline-flex items-center justify-center h-12 px-6 border border-[#0F2537] text-[#0F2537] hover:bg-slate-50 font-semibold text-sm tracking-wide rounded-sm"
                  data-testid="hero-cta-secondary"
                >
                  {t("hero.cta_secondary")}
                </a>
              </div>
              <dl className="mt-12 grid grid-cols-3 gap-6 border-t border-[#E2E8F0] pt-8">
                {[1, 2, 3].map((n) => (
                  <div key={n} data-testid={`hero-metric-${n}`}>
                    <dt className="text-[10px] sm:text-xs font-bold tracking-widest uppercase text-slate-500">
                      {t(`hero.metric_${n}_label`)}
                    </dt>
                    <dd className="mt-2 font-heading text-2xl sm:text-3xl font-bold text-slate-900">
                      {t(`hero.metric_${n}_value`)}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
            <div className="relative">
              <div className="border border-[#E2E8F0] shadow-sm bg-slate-50">
                <img
                  src="/images/hero-salt-flats.jpg"
                  alt={t("hero.image_alt")}
                  className="w-full h-[420px] lg:h-[560px] object-cover"
                  loading="eager"
                  data-testid="hero-image"
                />
              </div>
              <div className="absolute -bottom-6 left-6 right-6 lg:-bottom-8 lg:right-auto lg:left-8 bg-[#0F2537] text-white px-5 py-4 border-l-4 border-[#C85A32] shadow-lg">
                <p className="text-[10px] font-bold tracking-widest uppercase text-slate-300">
                  Salar de Olaroz — Jujuy, Argentina
                </p>
                <p className="mt-1 text-sm text-slate-100">
                  23&deg;29&#39;S 66&deg;45&#39;W · 3,900 m.s.n.m.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why */}
      <section id="sector" className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-16 lg:py-24">
          <div className="max-w-3xl">
            <p className="text-xs font-bold tracking-widest uppercase text-[#C85A32] mb-4">
              {t("why.eyebrow")}
            </p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900">
              {t("why.title")}
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-px bg-[#E2E8F0] border border-[#E2E8F0]">
            {Array.isArray(whyItems) &&
              whyItems.map((item, i) => (
                <div
                  key={i}
                  className="bg-white p-8 lg:p-10"
                  data-testid={`why-item-${i}`}
                >
                  <div className="flex items-baseline gap-4">
                    <span className="font-heading text-2xl font-bold text-[#C85A32]">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <h3 className="font-heading text-xl font-semibold text-slate-900">
                      {item.title}
                    </h3>
                  </div>
                  <p className="mt-3 text-sm lg:text-base text-slate-600 leading-relaxed">
                    {item.body}
                  </p>
                </div>
              ))}
          </div>
        </div>
      </section>

      {/* Sectors coverage */}
      <section className="bg-white border-b border-[#E2E8F0]">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-16 lg:py-24">
          <div className="max-w-3xl">
            <p className="text-xs font-bold tracking-widest uppercase text-[#C85A32] mb-4">
              {t("sectors.eyebrow")}
            </p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900">
              {t("sectors.title")}
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
            {Array.isArray(sectors) &&
              sectors.map((s, i) => (
                <article
                  key={i}
                  className="border border-[#E2E8F0] bg-white"
                  data-testid={`sector-card-${i}`}
                >
                  <div className="aspect-[4/3] w-full bg-slate-100 overflow-hidden">
                    <img
                      src={SECTOR_IMAGES[i]}
                      alt={s.image_alt}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-6 lg:p-7">
                    <p className="text-[10px] font-bold tracking-widest uppercase text-slate-500 mb-2">
                      {s.provinces}
                    </p>
                    <h3 className="font-heading text-xl font-semibold text-slate-900">
                      {s.region}
                    </h3>
                    <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                      {s.body}
                    </p>
                  </div>
                </article>
              ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section
        id="process"
        className="bg-[#0F2537] text-white border-b border-[#0F2537]"
      >
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-16 lg:py-24">
          <div className="max-w-3xl">
            <p className="text-xs font-bold tracking-widest uppercase text-[#C85A32] mb-4">
              {t("how.eyebrow")}
            </p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-white">
              {t("how.title")}
            </h2>
            <p className="mt-4 text-slate-300">{t("how.intro")}</p>
          </div>
          <ol className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-px bg-[#1a3550] border border-[#1a3550]">
            {Array.isArray(howSteps) &&
              howSteps.map((step, i) => (
                <li
                  key={i}
                  className="bg-[#0F2537] p-8 lg:p-10"
                  data-testid={`how-step-${i}`}
                >
                  <span className="font-mono text-xs tracking-widest text-[#C85A32]">
                    {step.n}
                  </span>
                  <h3 className="mt-3 font-heading text-xl font-semibold text-white">
                    {step.title}
                  </h3>
                  <p className="mt-3 text-sm text-slate-300 leading-relaxed">
                    {step.body}
                  </p>
                </li>
              ))}
          </ol>
        </div>
      </section>

      {/* Transparency */}
      <section id="transparency" className="bg-white border-b border-[#E2E8F0]">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 py-16 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <p className="text-xs font-bold tracking-widest uppercase text-[#C85A32] mb-4">
                {t("transparency.eyebrow")}
              </p>
              <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900">
                {t("transparency.title")}
              </h2>
              <p className="mt-5 text-slate-600 leading-relaxed">
                {t("transparency.body_1")}
              </p>
              <p className="mt-4 text-slate-600 leading-relaxed">
                {t("transparency.body_2")}
              </p>
              <ul className="mt-6 space-y-3">
                {Array.isArray(transparencyPoints) &&
                  transparencyPoints.map((p, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-3 text-sm text-slate-700"
                      data-testid={`transparency-point-${i}`}
                    >
                      <span className="mt-2 w-2 h-2 bg-[#C85A32] rounded-full flex-shrink-0" />
                      {p}
                    </li>
                  ))}
              </ul>
            </div>
            <div className="border border-[#E2E8F0] bg-slate-50 order-first lg:order-last">
              <img
                src="/images/dashboard.jpg"
                alt={t("transparency.image_alt")}
                className="w-full h-[360px] lg:h-[480px] object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section id="newsletter" className="bg-[#F8FAFC]">
        <div className="mx-auto max-w-5xl px-5 sm:px-8 lg:px-12 py-16 lg:py-24">
          <div className="max-w-2xl">
            <p className="text-xs font-bold tracking-widest uppercase text-[#C85A32] mb-4">
              {t("newsletter.eyebrow")}
            </p>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900">
              {t("newsletter.title")}
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              {t("newsletter.subtitle")}
            </p>
          </div>
          <div className="mt-10">
            <NewsletterForm />
          </div>
        </div>
      </section>
    </main>
  );
}
